import React, { useMemo, useState } from 'react';
import { Paperclip, PenSquare } from 'lucide-react';

import QuickAccessDrawer, {
	ListSkeleton,
	QuickAccessNotice
} from '@/features/crm-quick-access/components/QuickAccessDrawer';
import { counterpartyLabel, formatMailTimestamp } from '@/features/crm-quick-access/utils/format';

const FILTERS = [
	{ value: 'all', label: 'All' },
	{ value: 'unread', label: 'Unread' }
];

const MailDrawer = ({ open, onClose, dock, feed, openCrm }) => {
	const [filter, setFilter] = useState('all');
	const { account, threads } = feed;
	const connected = Boolean(account && account.status !== 'disconnected');
	const visibleThreads = useMemo(
		() => (filter === 'unread' ? threads.filter((thread) => thread.unreadCount > 0) : threads),
		[filter, threads]
	);

	const renderBody = () => {
		if (!feed.ready) return <ListSkeleton />;
		if (!connected) {
			return (
				<QuickAccessNotice
					title="Connect your mailbox"
					body="Connect Outlook in the CRM to see your inbox here."
					actionLabel="Connect Outlook"
					onAction={() => openCrm('/mail')}
				/>
			);
		}
		if (feed.threadsLoading && threads.length === 0) return <ListSkeleton />;
		if (feed.error && threads.length === 0) {
			return (
				<QuickAccessNotice
					title="Inbox unavailable"
					body={feed.error}
					actionLabel="Try again"
					onAction={() => feed.refresh(true)}
				/>
			);
		}
		if (visibleThreads.length === 0) {
			return (
				<QuickAccessNotice
					title={filter === 'unread' ? "You're all caught up" : 'Your inbox is empty'}
					body={
						filter === 'unread'
							? 'No unread conversations in your inbox.'
							: 'New conversations will show up here.'
					}
				/>
			);
		}
		return (
			<ul className="crm-qa-mail-list">
				{visibleThreads.map((thread) => {
					const unread = thread.unreadCount > 0;
					return (
						<li key={thread.threadKey}>
							<button
								type="button"
								className={`crm-qa-mail-row${unread ? ' crm-qa-mail-row--unread' : ''}`}
								onClick={() => openCrm(`/mail/inbox/${encodeURIComponent(thread.threadKey)}`)}
							>
								{unread && <span className="crm-qa-mail-row__dot" aria-label="Unread" />}
								<span className="crm-qa-mail-row__top">
									<span className="crm-qa-mail-row__from">
										{counterpartyLabel(thread)}
										{thread.messageCount > 1 && (
											<span className="crm-qa-mail-row__count">
												({thread.messageCount})
											</span>
										)}
									</span>
									<span className="crm-qa-mail-row__time">
										{formatMailTimestamp(thread.receivedAt ?? thread.sentAt)}
									</span>
								</span>
								<span className="crm-qa-mail-row__subject">
									<span className="crm-qa-truncate">
										{thread.subject?.trim() || '(no subject)'}
									</span>
									{thread.hasAttachments && <Paperclip size={12} />}
								</span>
								{thread.preview && (
									<span className="crm-qa-mail-row__preview">{thread.preview}</span>
								)}
							</button>
						</li>
					);
				})}
			</ul>
		);
	};

	return (
		<QuickAccessDrawer
			open={open}
			onClose={onClose}
			dock={dock}
			eyebrow="Mail"
			title={feed.unreadCount > 0 ? `Inbox · ${feed.unreadCount} unread` : 'Inbox'}
			subtitle={account?.emailAddress}
			refreshing={feed.threadsLoading}
			onRefresh={() => feed.refresh(true)}
			toolbar={
				connected && (
					<div className="crm-qa-segmented">
						{FILTERS.map((option) => (
							<button
								key={option.value}
								type="button"
								className={filter === option.value ? 'active' : ''}
								onClick={() => setFilter(option.value)}
							>
								{option.label}
							</button>
						))}
					</div>
				)
			}
			fullViewLabel="Open Mail"
			onOpenFullView={() => openCrm('/mail/inbox')}
			footerExtra={
				connected && (
					<button
						type="button"
						className="crm-qa-secondary-button"
						onClick={() => openCrm('/mail?compose=1')}
					>
						<PenSquare size={14} />
						Compose
					</button>
				)
			}
		>
			{renderBody()}
		</QuickAccessDrawer>
	);
};

export default MailDrawer;
